import type { FieldContextValue } from './field-context.js'
import { ContextProvider } from '@lit/context'
import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { StateAttributesController } from '../common/controllers/StateAttributesController.js'
import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { fieldContext } from './field-context.js'
import style from './Field.css'

/** Per-instance counters so generated label/helper ids stay unique. */
let fieldHelperId = 0
let fieldLabelId = 0

/**
 * Field is the wrapper for a single form control. It lays out a
 * [Field Label](/components/field-label/), a control (such as an
 * [Input](/components/input/)), an optional
 * [Field Description](/components/field-description/) and a
 * [Field Error](/components/field-error/) with consistent spacing.
 *
 * Combine several together in a [Field Group](/components/field-group/) and
 * group related groups with a [Field Set](/components/field-set/).
 *
 * The Field shares its state with the control through context: a Nord control
 * placed inside a Field suppresses its own built-in label/hint/error and takes
 * its accessible name, description and invalid/disabled state from the Field.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @slot - Default slot for the field parts (label, control, content, description, error).
 *
 * @attr {boolean} data-invalid - Present while the field is invalid (mirrors the `invalid` property). Style hook for the whole field.
 * @attr {boolean} data-disabled - Present while the field is disabled (mirrors the `disabled` property).
 * @attr {boolean} data-focused - Present while the field's control has focus. Style hook for focus-within treatments across the field.
 *
 * @cssprop [--n-field-gap=var(--n-space-s)] - Controls the spacing between the field parts, using our [spacing tokens](/tokens/#space).
 */
@customElement('nord-field')
export default class Field extends DraftComponentMixin(LitElement) {
  /**
   * The layout of the field.
   *
   * - `vertical` (default) stacks the label, control and helper text.
   * - `horizontal` places the label and control side by side, vertically centered.
   * - `responsive` lays out horizontally in a wide enough container, otherwise vertical.
   */
  @property({ reflect: true }) orientation: 'vertical' | 'horizontal' | 'responsive' = 'vertical'

  /**
   * Marks the field as invalid, enabling error styling hooks. Also reflected as
   * `data-invalid` and propagated to the control via context.
   */
  @property({ reflect: true, type: Boolean }) invalid = false

  /**
   * Disables the field and, via context, its control. Reflected as `data-disabled`.
   */
  @property({ reflect: true, type: Boolean }) disabled = false

  /** Provides {@link fieldContext} to the control inside this field. */
  private provider = new ContextProvider(this, { context: fieldContext, initialValue: undefined })

  /**
   * The control's published `required`, used to drive the label's indicator.
   * Not reactive — it only toggles an attribute imperatively via syncLabelIndicator.
   */
  private controlRequired = false

  /**
   * Reflects the field's state onto `data-*` attributes (the Lit equivalent of
   * Base UI's `stateAttributesMapping`): `data-invalid`/`data-disabled` from the
   * properties and `data-focused` tracked automatically.
   */
  private stateAttributes = new StateAttributesController(
    this,
    () => ({ invalid: this.invalid, disabled: this.disabled }),
    { trackFocus: true },
  )

  /** Observes child additions/removals so the shared context stays in sync. */
  private childObserver?: MutationObserver
  /** Observes an enclosing Field Set's `disabled` so the cascade stays in sync. */
  private fieldSetObserver?: MutationObserver
  /** `aria-describedby` ids this field added to a native control on its last run. */
  private managedDescribedBy: string[] = []

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)

    // Re-publish context whenever children change (e.g. an error or label is
    // added/removed, or a generated id lands).
    this.childObserver ??= new MutationObserver(() => this.updateContext())
    this.childObserver.observe(this, { characterData: true, childList: true, subtree: true })

    const fieldSet = this.closest('nord-field-set')
    if (fieldSet) {
      this.fieldSetObserver ??= new MutationObserver(() => this.updateContext())
      this.fieldSetObserver.observe(fieldSet, { attributes: true, attributeFilter: ['disabled'] })
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.childObserver?.disconnect()
    this.childObserver = undefined
    this.fieldSetObserver?.disconnect()
    this.fieldSetObserver = undefined
  }

  protected createRenderRoot() {
    return this
  }

  protected firstUpdated() {
    this.updateContext()
  }

  protected updated(changed: Map<string, unknown>) {
    if (changed.has('invalid') || changed.has('disabled')) {
      this.updateContext()
    }
  }

  /** The control publishes its `required` here; we drive the label indicator. */
  private setRequired = (required: boolean) => {
    this.controlRequired = required
    this.syncLabelIndicator()
  }

  /**
   * Recompute and publish the shared context. The provider compares by identity,
   * so we always hand it a fresh object.
   */
  private updateContext() {
    const fieldSet = this.closest('nord-field-set') as (HTMLElement & { disabled?: boolean }) | null

    const describedByElements = this.collectDescribedByElements()
    const value: FieldContextValue = {
      invalid: this.invalid,
      disabled: this.disabled || Boolean(fieldSet?.disabled),
      labelId: this.resolveLabelId(),
      labelText: this.resolveLabelText(),
      describedById: this.collectDescribedById(describedByElements),
      describedByElements,
      setRequired: this.setRequired,
    }

    this.provider.setValue(value)
    this.syncLabelIndicator()
    this.linkNativeControl(value)
  }

  private get labelElement(): HTMLElement | null {
    return this.querySelector('nord-field-label')
  }

  /** Ensure the label has an id (for `aria-labelledby` on light-DOM controls). */
  private resolveLabelId(): string | undefined {
    const label = this.labelElement
    if (!label) {
      return undefined
    }
    if (!label.id) {
      label.id = `n-field-label-${fieldLabelId++}`
    }
    return label.id
  }

  /** The label text (for `aria-label` on shadow-DOM controls). */
  private resolveLabelText(): string | undefined {
    return this.labelElement?.textContent?.trim() || undefined
  }

  /**
   * Collect the ids of any description/error elements (in DOM order, generating
   * ids as needed) for the control's `aria-describedby`.
   */
  private collectDescribedByElements(): HTMLElement[] {
    return Array.from(this.querySelectorAll<HTMLElement>('nord-field-description, nord-field-error'))
  }

  private collectDescribedById(helpers: HTMLElement[]): string | undefined {
    const ids = helpers.map((helper) => {
      if (!helper.id) {
        helper.id = `n-field-helper-${fieldHelperId++}`
      }
      return helper.id
    })
    return ids.length ? ids.join(' ') : undefined
  }

  private syncLabelIndicator() {
    this.labelElement?.toggleAttribute('required', this.controlRequired)
  }

  /**
   * Context only reaches Nord controls (consumers). A native `<input>`/`<select>`/
   * `<textarea>` placed directly in a Field is wired here instead — light-DOM
   * IDREFs resolve in-tree. This is the only remaining imperative path, mirroring
   * Base UI's `Field.Control` being for native elements.
   */
  private linkNativeControl(value: FieldContextValue) {
    const native = this.querySelector<HTMLElement>(
      ':scope > input, :scope > select, :scope > textarea, :scope > [contenteditable]',
    )
    if (!native) {
      return
    }

    if (value.labelId) {
      native.setAttribute('aria-labelledby', value.labelId)
    }

    // Merge our helper ids with any consumer-set aria-describedby rather than
    // clobbering it; drop the ids we added on a previous run.
    const managed = value.describedById ? value.describedById.split(/\s+/) : []
    const current = (native.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean)
    const preserved = current.filter(token => !this.managedDescribedBy.includes(token) && !managed.includes(token))
    this.managedDescribedBy = managed
    const describedBy = [...preserved, ...managed].join(' ')
    if (describedBy) {
      native.setAttribute('aria-describedby', describedBy)
    }
    else {
      native.removeAttribute('aria-describedby')
    }

    if (value.invalid) {
      native.setAttribute('aria-invalid', 'true')
    }
    else {
      native.removeAttribute('aria-invalid')
    }

    // Surface a natively-required control on the label indicator.
    this.setRequired(native.matches('[required]'))
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-field': Field
  }
}
